import {
  collection,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { TaskEntry } from './CalendarTypes';

export class TaskService {
  static async fetchTaskEntries(
    companyId: string,
    selectedDate: string,
    selectedUserId: string
  ): Promise<TaskEntry[]> {
    if (!companyId || !selectedDate || !selectedUserId) return [];

    try {
      const tasksRef = collection(db, `companies/${companyId}/tasks`);
      const selectedDateStart = new Date(selectedDate);
      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);
      
      // Filter tasks by selected user and date range (both done and pending)
      const tasksQuery = query(
        tasksRef,
        where('userId', '==', selectedUserId),
        where('dueDate', '>=', Timestamp.fromDate(selectedDateStart)),
        where('dueDate', '<=', Timestamp.fromDate(selectedDateEnd)),
        orderBy('dueDate', 'asc')
      );

      const snapshot = await getDocs(tasksQuery);
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        entryType: 'task'
      })) as TaskEntry[];
      
      // Fetch lead and user details for each task
      const enhancedTasks = await Promise.all(tasksData.map(async (task) => {
        try {
          // Get lead name using leadId
          if (task.leadId) {
            const leadRef = doc(db, `companies/${companyId}/leads`, task.leadId);
            const leadDoc = await getDoc(leadRef);
            if (leadDoc.exists()) {
              const leadData = leadDoc.data();
              task.leadName = leadData.name || 'Unknown Lead';
            }
          }
          
          // Get assigned by user name using assignedBy
          if (task.assignedBy) {
            const userRef = doc(db, 'users', task.assignedBy);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              task.assignedByName = userData.name || userData.email || 'Unknown User';
            }
          }
          
          return task;
        } catch (error) {
          console.error('Error fetching details for task:', error);
          return task;
        }
      }));

      return enhancedTasks;
    } catch (error) {
      console.error('Error fetching task entries:', error);
      return [];
    }
  }

  static async markTaskAsDone(
    companyId: string,
    taskId: string
  ): Promise<void> {
    try {
      // Update the task to mark as done
      const taskRef = doc(db, `companies/${companyId}/tasks`, taskId);
      await updateDoc(taskRef, {
        isDone: true,
        completedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error marking task as done:', error);
      throw error;
    }
  }
}
